import {
  type SemanticLegendSettingsWithCategoricalConfig,
  YAxisValues,
} from '../../types';
import {DEFAULT_SEMANTIC_LEGEND_SETTINGS} from '../SemanticLegendDefaults';
import {
  SEMANTIC_LEGEND_PALETTE_CATEGORICAL_CONFIG_DEFAULT,
  SEMANTIC_LEGEND_PALETTE_DEFAULT,
  type SemanticLegendPalette,
} from './colorPalettes';
import {getColorFromValue} from './getDefaultBucketColors';

export type SemanticLegendRuntimeValue = number | string;

export const EXCLUDED_BUCKET_ID = 'excluded';

export type ScatterSemanticBucketAssignment = {
  bucketId: string;
  bucketType: 'numeric' | 'categorical';
  bucketIndex?: number;
  bucketLabel: string;
  sourceKey: string;
  sourceValue?: SemanticLegendRuntimeValue;
  color: string;
  isExcluded?: boolean;
};

type ResolveSemanticLegendAssignmentArgs = {
  categoryIndexByValue?: Map<string, number>;
  semanticLegendPalette?: SemanticLegendPalette;
  settings: Pick<
    SemanticLegendSettingsWithCategoricalConfig,
    | 'key'
    | 'buckets'
    | 'bucketCustomizations'
    | 'excludedRunColor'
    | 'selectedCategories'
    | 'yAxisValue'
  >;
  value: SemanticLegendRuntimeValue | undefined;
};

type SemanticLegendBucket = NonNullable<
  SemanticLegendSettingsWithCategoricalConfig['buckets']
>[number];

export function normalizeNumericSemanticValue(
  value: unknown
): SemanticLegendRuntimeValue | undefined {
  if (typeof value === 'number') {
    return value;
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'boolean') {
    return value ? 1 : 0;
  }
  return undefined;
}

export function resolveSemanticLegendAssignment({
  categoryIndexByValue,
  semanticLegendPalette,
  settings,
  value,
}: ResolveSemanticLegendAssignmentArgs): ScatterSemanticBucketAssignment {
  if (settings.yAxisValue === YAxisValues.Config_Categories) {
    return getCategoricalAssignment({
      categoryIndexByValue,
      semanticLegendPalette,
      settings,
      value,
    });
  }
  return getNumericAssignment({
    categoryIndexByValue,
    semanticLegendPalette,
    settings,
    value,
  });
}

function getCategoricalAssignment({
  categoryIndexByValue,
  semanticLegendPalette,
  settings,
  value,
}: ResolveSemanticLegendAssignmentArgs): ScatterSemanticBucketAssignment {
  if (value == null) {
    return excludedAssignment(value, settings.excludedRunColor, settings);
  }
  const {bucketIndex, category} = getCategoryBucket(
    settings,
    value,
    categoryIndexByValue
  );
  if (category == null || bucketIndex === -1) {
    return excludedAssignment(value, settings.excludedRunColor, settings);
  }

  return {
    bucketId: getCategoricalBucketId(category),
    bucketType: 'categorical',
    bucketIndex,
    bucketLabel: category,
    sourceKey: settings.key ?? '',
    sourceValue: category,
    color: getCategoryColor(settings, bucketIndex, semanticLegendPalette),
    isExcluded: false,
  };
}

function getNumericAssignment({
  semanticLegendPalette,
  settings,
  value,
}: ResolveSemanticLegendAssignmentArgs): ScatterSemanticBucketAssignment {
  if (value == null) {
    return excludedAssignment(value, settings.excludedRunColor, settings);
  }
  const numericBucket = getNumericBucket(settings, value);
  if (numericBucket == null) {
    return excludedAssignment(value, settings.excludedRunColor, settings);
  }
  const {bucket, bucketIndex} = numericBucket;

  return {
    bucketId: getNumericBucketId(bucketIndex),
    bucketType: 'numeric',
    bucketIndex,
    bucketLabel: `${formatSemanticNumber(bucket.bucketMin)} - ${formatSemanticNumber(
      bucket.bucketMax
    )}`,
    sourceKey: settings.key ?? '',
    sourceValue: value,
    color: getNumericColor(settings, numericBucket, semanticLegendPalette),
    isExcluded: false,
  };
}

function getCategoryBucket(
  settings: ResolveSemanticLegendAssignmentArgs['settings'],
  value: SemanticLegendRuntimeValue | undefined,
  categoryIndexByValue?: Map<string, number>
): {bucketIndex: number; category?: string} {
  const category = value == null ? undefined : `${value}`;
  if (category == null) {
    return {bucketIndex: -1};
  }
  if (categoryIndexByValue != null) {
    const bucketIndex = categoryIndexByValue.get(category);
    return {bucketIndex: bucketIndex ?? -1, category};
  }
  return {
    bucketIndex:
      category == null
        ? -1
        : (settings.selectedCategories ?? []).findIndex(
            selectedCategory => selectedCategory === category
          ),
    category,
  };
}

function getCategoryColor(
  settings: ResolveSemanticLegendAssignmentArgs['settings'],
  bucketIndex: number,
  semanticLegendPalette: SemanticLegendPalette | undefined
): string {
  return (
    settings.bucketCustomizations?.[bucketIndex]?.color ??
    getColorFromValue(
      bucketIndex,
      settings.selectedCategories?.length ?? 0,
      semanticLegendPalette ??
        SEMANTIC_LEGEND_PALETTE_CATEGORICAL_CONFIG_DEFAULT
    )
  );
}

function getNumericBucket(
  settings: ResolveSemanticLegendAssignmentArgs['settings'],
  value: SemanticLegendRuntimeValue | undefined
): {bucket: SemanticLegendBucket; bucketIndex: number} | undefined {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return undefined;
  }

  const buckets = settings.buckets ?? [];
  if (buckets.length === 0) {
    return undefined;
  }

  const foundBucketIndex = buckets.findIndex(
    bucket => numericValue <= (bucket.bucketMax ?? 0)
  );
  const bucketIndex =
    foundBucketIndex === -1 ? buckets.length - 1 : foundBucketIndex;

  return {
    bucket: buckets[bucketIndex],
    bucketIndex,
  };
}

function getNumericColor(
  settings: ResolveSemanticLegendAssignmentArgs['settings'],
  {bucket, bucketIndex}: {bucket: SemanticLegendBucket; bucketIndex: number},
  semanticLegendPalette: SemanticLegendPalette | undefined
): string {
  return (
    settings.bucketCustomizations?.[bucketIndex]?.color ??
    bucket.color ??
    getColorFromValue(
      bucketIndex,
      settings.buckets?.length ?? 0,
      semanticLegendPalette ?? SEMANTIC_LEGEND_PALETTE_DEFAULT
    )
  );
}

function excludedAssignment(
  value: SemanticLegendRuntimeValue | undefined,
  color: string | undefined,
  settings: ResolveSemanticLegendAssignmentArgs['settings']
): ScatterSemanticBucketAssignment {
  const bucket: ScatterSemanticBucketAssignment = {
    bucketId: EXCLUDED_BUCKET_ID,
    bucketType: getExcludedBucketType(settings.yAxisValue),
    bucketLabel: 'Excluded / missing',
    sourceKey: settings.key ?? '',
    color: getExcludedColor(color),
    isExcluded: true,
  };
  if (value !== undefined) {
    bucket.sourceValue = value;
  }
  return bucket;
}

function getExcludedBucketType(
  yAxisValue: ResolveSemanticLegendAssignmentArgs['settings']['yAxisValue']
): ScatterSemanticBucketAssignment['bucketType'] {
  return yAxisValue === YAxisValues.Config_Categories
    ? 'categorical'
    : 'numeric';
}

function getExcludedColor(color: string | undefined): string {
  return color ?? DEFAULT_SEMANTIC_LEGEND_SETTINGS.excludedRunColor;
}

function formatSemanticNumber(value: number | undefined): string {
  if (value == null) {
    return '';
  }
  return Number.isInteger(value) ? `${value}` : value.toPrecision(4);
}

function getNumericBucketId(bucketIndex: number): string {
  return `numeric:${bucketIndex}`;
}

function getCategoricalBucketId(category: string): string {
  return `categorical:${category}`;
}
